import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import BASE_URL from '../../url';
import '../styles/erprecruitment.css';
import { 
  Calendar, 
  Search, 
  Users, 
  Download, 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  RotateCcw, 
  User, 
  X,
  Loader,
  Building2,
  Globe,
  Zap,
  BarChart3,
  SlidersHorizontal,
  FileText,
  FileSpreadsheet,
  Lightbulb,
  Check,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { generateExecutiveDeckHtml } from '../../Submissions/utils/reportExportUtils';
import { generateRecruiterPerformanceReportHtml } from '../utils/recruiterReportUtils';

const SESSION_KEY = 'recruiter_perf_filters_v2';

const toYMD = (d) => {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ─── Searchable Recruiter Dropdown Component ─────────────────────────────────────
const SearchableRecruiterSelect = ({ recruiters = [], selectedRecruiter, onSelect, region = 'Onsite' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isOffshore = String(region).toLowerCase() === 'offshore';

  const filteredRecruiters = (recruiters || []).filter(r =>
    (r.name || r.username || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="input-group" ref={containerRef} style={{ position: "relative", minWidth: "220px", flex: "1 1 200px", maxWidth: "300px" }}>
      <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: 700, color: "#475569", marginBottom: "4px" }}>
        <Filter size={12} color="#3b5bfd" />
        Recruiter Filter
      </label>
      <div
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "6px 12px", background: "white", border: "1px solid #cbd5e1",
          borderRadius: "6px", cursor: "pointer", fontSize: "12px", color: "#1e293b",
          height: "34px", boxSizing: "border-box"
        }}
      >
        <span style={{ fontWeight: selectedRecruiter ? 700 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: selectedRecruiter ? "#3b5bfd" : "#1e293b", display: "flex", alignItems: "center", gap: "6px" }}>
          {selectedRecruiter ? (
            <>
              <User size={13} color="#3b5bfd" /> {selectedRecruiter}
            </>
          ) : (
            <>
              {isOffshore ? <Globe size={13} color="#3b5bfd" /> : <Building2 size={13} color="#3b5bfd" />} All {region} Recruiters
            </>
          )}
        </span>
        <ChevronDown size={13} color="#64748b" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", marginLeft: "6px", flexShrink: 0 }} />
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, marginTop: "4px",
            width: "260px", background: "white", border: "1px solid #cbd5e1",
            borderRadius: "8px", boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
            zIndex: 9999, overflow: "hidden"
          }}
        >
          {/* Search Input Box */}
          <div style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: "6px", background: "#f8fafc" }}>
            <Search size={13} color="#94a3b8" />
            <input
              type="text"
              autoFocus
              placeholder="Search recruiter..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", border: "none", outline: "none", background: "transparent",
                fontSize: "12px", color: "#1e293b"
              }}
            />
            {search && (
              <X size={12} color="#94a3b8" style={{ cursor: "pointer" }} onClick={() => setSearch("")} />
            )}
          </div>

          {/* Options List */}
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            <div
              onClick={() => { onSelect(""); setIsOpen(false); setSearch(""); }}
              style={{
                padding: "7px 12px", fontSize: "12px", cursor: "pointer",
                background: !selectedRecruiter ? "#f0fdf4" : "transparent",
                color: !selectedRecruiter ? "#166534" : "#334155",
                fontWeight: !selectedRecruiter ? 700 : 500,
                borderBottom: "1px solid #f1f5f9",
                display: "flex", alignItems: "center", gap: "6px"
              }}
            >
              {isOffshore ? <Globe size={13} color="#3b5bfd" /> : <Building2 size={13} color="#3b5bfd" />}
              <span>All {region} Recruiters ({(recruiters || []).length})</span>
            </div>
            {filteredRecruiters.length === 0 ? (
              <div style={{ padding: "10px", fontSize: "11px", color: "#94a3b8", textAlign: "center" }}>No recruiters found</div>
            ) : (
              filteredRecruiters.map((r, idx) => {
                const name = r.name || r.username;
                const isSelected = selectedRecruiter === name;
                const isBench = Boolean(r.isBench || (r.name && ['abinesh anbazhagan', 'pragadeeshwaran karthikeyan', 'karunakaran palani', 'rohinth chelladurai'].some(b => r.name.toLowerCase().includes(b))));
                return (
                  <div
                    key={idx}
                    onClick={() => { onSelect(name); setIsOpen(false); setSearch(""); }}
                    style={{
                      padding: "7px 12px", fontSize: "12px", cursor: "pointer",
                      background: isSelected ? "#e0f2fe" : "transparent",
                      color: isSelected ? "#0369a1" : "#334155",
                      fontWeight: isSelected ? 700 : 400,
                      display: "flex", alignItems: "center", justifyContent: "space-between"
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <User size={12} color="#64748b" /> {name}
                      {isBench && (
                        <span style={{
                          marginLeft: '4px',
                          background: '#ffedd5',
                          color: '#c2410c',
                          padding: '1px 5px',
                          borderRadius: '3px',
                          fontSize: '9.5px',
                          fontWeight: 700,
                          border: '1px solid #fed7aa'
                        }}>
                          Bench
                        </span>
                      )}
                    </span>
                    {isSelected && <Check size={13} color="#0369a1" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RecruiterPerformanceReport = () => {
  const navigate = useNavigate();
  // Read initial filters from sessionStorage
  const getStoredFilters = () => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn("Could not read sessionStorage:", e);
    }
    return {
      region: 'Onsite',
      timeframe: 'All Time',
      quickDate: 'all',
      startDate: '',
      endDate: '',
      selectedRecruiter: ''
    };
  };

  const initialFilters = getStoredFilters();

  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recruitersList, setRecruitersList] = useState([]);
  const [error, setError] = useState('');

  // Filters State
  const [region, setRegion] = useState(initialFilters.region || 'Onsite');
  const [timeframe, setTimeframe] = useState(initialFilters.timeframe || 'All Time');
  const [quickDate, setQuickDate] = useState(initialFilters.quickDate || 'all');
  const [startDate, setStartDate] = useState(initialFilters.startDate || '');
  const [endDate, setEndDate] = useState(initialFilters.endDate || '');
  const [selectedRecruiter, setSelectedRecruiter] = useState(initialFilters.selectedRecruiter || '');

  // Sorting state for Recruiter Breakdown table
  const [sortConfig, setSortConfig] = useState({ key: 'recruiterName', direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'desc';
    if (key === 'recruiterName') {
      direction = 'asc';
    }
    if (sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    setSortConfig({ key, direction });
  };

  const truncateRecruiterName = (name) => {
    if (!name) return '';
    return name;
  };

  const renderSortHeader = (key, label, width, align = 'center') => {
    const isSorted = sortConfig.key === key;
    return (
      <th
        key={key}
        onClick={() => handleSort(key)}
        style={{
          padding: '10px 6px',
          fontSize: '12.5px',
          fontWeight: 650,
          color: isSorted ? '#2546e5' : '#475569',
          textAlign: align,
          width: width || 'auto',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'color 0.15s ease',
          whiteSpace: 'nowrap'
        }}
        title={`Click to sort by ${label}`}
        onMouseEnter={(e) => {
          if (!isSorted) e.currentTarget.style.color = '#1e2432';
        }}
        onMouseLeave={(e) => {
          if (!isSorted) e.currentTarget.style.color = '#475569';
        }}
      >
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          justifyContent: align === 'center' ? 'center' : 'flex-start'
        }}>
          <span>{label}</span>
          {isSorted ? (
            sortConfig.direction === 'asc' 
              ? <ArrowUp size={12} color="#2546e5" strokeWidth={2.5} /> 
              : <ArrowDown size={12} color="#2546e5" strokeWidth={2.5} />
          ) : (
            <ArrowUpDown size={11} color="#94a3b8" style={{ opacity: 0.6 }} />
          )}
        </div>
      </th>
    );
  };


  // Save to Session Storage whenever filters change
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        region,
        timeframe,
        quickDate,
        startDate,
        endDate,
        selectedRecruiter
      }));
    } catch (e) {
      console.warn("Could not save to sessionStorage:", e);
    }
  }, [region, timeframe, quickDate, startDate, endDate, selectedRecruiter]);

  // Drill-down State
  const [expandedRecruiter, setExpandedRecruiter] = useState(null);
  const [candidateSubmissions, setCandidateSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [hiringStages, setHiringStages] = useState([]);
  const [updatingStageId, setUpdatingStageId] = useState(null);

  // Download Dropdown State
  const [isReportDropdownOpen, setIsReportDropdownOpen] = useState(false);
  const reportDropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (reportDropdownRef.current && !reportDropdownRef.current.contains(e.target)) {
        setIsReportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const [benchRecruitersList, setBenchRecruitersList] = useState([]);

  useEffect(() => {
    const fetchBenchRecruiters = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/recruitment/roles/bench-recruiters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          setBenchRecruitersList(res.data.data.map(b => (b.name || '').toLowerCase().trim()));
        }
      } catch (e) {
        console.error("Failed to fetch bench recruiters list:", e);
      }
    };
    fetchBenchRecruiters();
  }, []);

  useEffect(() => {
    const fetchHiringStages = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/recruitment/applications/hiring-steps`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (Array.isArray(res.data)) {
          setHiringStages(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch hiring stages:', err);
      }
    };
    fetchHiringStages();
  }, []);

  const isBenchRecruiter = (recruiterName) => {
    if (!recruiterName) return false;
    const lower = recruiterName.toLowerCase().trim();
    if (benchRecruitersList.some(b => b === lower || lower.includes(b) || b.includes(lower))) return true;
    return ['abinesh anbazhagan', 'pragadeeshwaran karthikeyan', 'karunakaran palani', 'rohinth chelladurai'].some(b => lower.includes(b));
  };

  // 1. Fetch Recruiters for Dropdown based on selected Region
  useEffect(() => {
    const fetchRecruiters = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/recruitment/roles/recruiters-by-region?region=${region}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data && res.data.success) {
          setRecruitersList(res.data.data || []);
        } else if (Array.isArray(res.data)) {
          setRecruitersList(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch recruiters list for region:", region, err);
      }
    };
    fetchRecruiters();
  }, [region]);

  // 2. Fetch Main Performance Report Data
  const fetchReportData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      let url = `${BASE_URL}/api/recruitment/roles/performance-report?region=${region}&timeframe=${timeframe}`;

      if (startDate && endDate) {
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }
      if (selectedRecruiter) {
        url += `&recruiter=${encodeURIComponent(selectedRecruiter)}`;
      }

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data && res.data.success) {
        setReportData(res.data.data || []);
      } else {
        setReportData([]);
      }
    } catch (err) {
      console.error("Failed to fetch performance report", err);
      setError('Failed to fetch performance report data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line
  }, [region, timeframe, selectedRecruiter, startDate, endDate]);

  // Switch Region (Onsite / Offshore)
  const handleRegionChange = (newRegion) => {
    if (newRegion === region) return;
    setRegion(newRegion);
    setSelectedRecruiter('');
    setExpandedRecruiter(null);
    setCandidateSubmissions([]);
  };

  // 3. Quick Date Picker Handler
  const handleQuickDateSelect = (type) => {
    const today = new Date();
    let start = '';
    let end = '';

    if (type === 'all') {
      start = '';
      end = '';
    } else if (type === 'today') {
      start = toYMD(today);
      end = toYMD(today);
    } else if (type === 'yesterday') {
      const yest = new Date(today);
      yest.setDate(yest.getDate() - 1);
      start = toYMD(yest);
      end = toYMD(yest);
    } else if (type === 'this_week') {
      const d = new Date(today);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(d.setDate(diff));
      start = toYMD(monday);
      end = toYMD(today);
    } else if (type === 'last_week') {
      const d = new Date(today);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1) - 7;
      const monday = new Date(d.setDate(diff));
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      start = toYMD(monday);
      end = toYMD(sunday);
    } else if (type === 'this_month') {
      start = toYMD(new Date(today.getFullYear(), today.getMonth(), 1));
      end = toYMD(today);
    } else if (type === 'last_month') {
      start = toYMD(new Date(today.getFullYear(), today.getMonth() - 1, 1));
      end = toYMD(new Date(today.getFullYear(), today.getMonth(), 0));
    }

    setQuickDate(type);
    setStartDate(start);
    setEndDate(end);
  };

  const handleClearFilters = () => {
    setQuickDate('all');
    setStartDate('');
    setEndDate('');
    setSelectedRecruiter('');
    setTimeframe('All Time');
    setExpandedRecruiter(null);
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch (e) {
      console.warn("Could not clear sessionStorage:", e);
    }
  };

  // 4. Drill-Down Submissions Fetch
  const toggleRecruiterSubmissions = async (recruiterName) => {
    if (expandedRecruiter === recruiterName) {
      setExpandedRecruiter(null);
      setCandidateSubmissions([]);
      return;
    }

    setExpandedRecruiter(recruiterName);
    setLoadingSubmissions(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${BASE_URL}/api/recruitment/roles/recruiter-submissions?recruiter=${encodeURIComponent(recruiterName)}&region=${region}`;
      if (startDate && endDate) {
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data && res.data.success) {
        setCandidateSubmissions(res.data.data || []);
      } else {
        setCandidateSubmissions([]);
      }
    } catch (err) {
      console.error("Error fetching recruiter candidates:", err);
      setCandidateSubmissions([]);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  // Progressive hiring stages for stage selection modal
  const progressiveStages = useMemo(() => {
    return (hiringStages || []).filter(s => {
      const code = (s.stageCode || '').toUpperCase();
      const name = (s.stepName || '').toLowerCase().trim();
      return !['REJECTED', 'WITHDRAWN', 'REJECTED_INTERNAL', 'WITHDRAWN_INTERNAL', 'HOLD', 'ON_HOLD', 'HOLD_INTERNAL'].includes(code) &&
             !name.includes('rejected') && !name.includes('withdrawn') && !name.includes('hold');
    });
  }, [hiringStages]);

  // 5. Update Candidate Current Stage from Drill-Down Row (Submit Profile? Style Modal)
  const handleOpenStageModal = async (cand) => {
    if (!cand || !cand.id) return;
    const stagesToUse = progressiveStages.length > 0 ? progressiveStages : hiringStages;
    if (!stagesToUse || stagesToUse.length === 0) {
      Swal.fire('No Stages', 'Hiring stages are not available yet.', 'info');
      return;
    }

    const candName = cand.candidateName || 'Candidate';
    const roleTitle = cand.jobTitle || 'Role';
    const clientName = cand.clientName ? ` (${cand.clientName})` : '';

    // Find current index
    const currentIdx = stagesToUse.findIndex((s, idx) =>
      (cand.stageCode && s.stageCode === cand.stageCode) ||
      (cand.currentStageName && s.stepName?.toLowerCase() === cand.currentStageName.toLowerCase()) ||
      (cand.currentStep !== undefined && (s.stepIndex === cand.currentStep || idx === cand.currentStep))
    );
    const initialSelectedIdx = currentIdx >= 0 ? currentIdx : 0;
    const initialStage = stagesToUse[initialSelectedIdx] || {};

    let stagesListHtml = '';
    stagesToUse.forEach((stage, idx) => {
      const isCurrent = idx === initialSelectedIdx;
      stagesListHtml += `
        <div 
          class="swal-stage-row" 
          id="swal-stage-row-${idx}"
          data-idx="${idx}" 
          style="display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; margin-bottom: 5px; border-radius: 7px; border: 1.5px solid ${isCurrent ? '#0d9488' : '#e2e8f0'}; background: ${isCurrent ? '#f0fdfa' : '#ffffff'}; cursor: pointer; transition: all 0.15s ease;"
        >
          <div class="swal-stage-select-target" data-idx="${idx}" style="display: flex; align-items: center; gap: 9px; flex: 1; text-align: left;">
            <input 
              type="radio" 
              name="swal_selected_stage_radio" 
              id="swal-stage-radio-${idx}" 
              value="${idx}" 
              ${isCurrent ? 'checked' : ''} 
              style="accent-color: #0d9488; width: 16px; height: 16px; cursor: pointer;"
            />
            <div>
              <div style="font-size: 13px; font-weight: 700; color: #1e293b;">
                ${idx + 1}. ${stage.stepName || stage.stageCode}
              </div>
              ${stage.phaseCode ? `<div style="font-size: 10px; font-weight: 600; color: #64748b; text-transform: uppercase;">${stage.phaseCode} PHASE</div>` : ''}
            </div>
          </div>
          <div style="display: flex; align-items: center; gap: 5px; flex-shrink: 0;">
            <button 
              type="button" 
              class="swal-stage-action-btn swal-stage-reject-btn" 
              id="swal-reject-btn-${idx}"
              data-idx="${idx}" 
              title="Reject candidate at ${stage.stepName || stage.stageCode}" 
              style="display: inline-flex; align-items: center; gap: 2px; padding: 4px 7px; font-size: 11px; font-weight: 700; border-radius: 5px; border: 1px solid #fca5a5; background: #ffffff; color: #dc2626; cursor: pointer; transition: all 0.15s ease;"
            >
              🔴 Reject
            </button>
            <button 
              type="button" 
              class="swal-stage-action-btn swal-stage-hold-btn" 
              id="swal-hold-btn-${idx}"
              data-idx="${idx}" 
              title="Hold candidate at ${stage.stepName || stage.stageCode}" 
              style="display: inline-flex; align-items: center; gap: 2px; padding: 4px 7px; font-size: 11px; font-weight: 700; border-radius: 5px; border: 1px solid #c4b5fd; background: #ffffff; color: #7c3aed; cursor: pointer; transition: all 0.15s ease;"
            >
              ⏸ Hold
            </button>
            <button 
              type="button" 
              class="swal-stage-action-btn swal-stage-withdraw-btn" 
              id="swal-withdraw-btn-${idx}"
              data-idx="${idx}" 
              title="Withdraw candidate at ${stage.stepName || stage.stageCode}" 
              style="display: inline-flex; align-items: center; gap: 2px; padding: 4px 7px; font-size: 11px; font-weight: 700; border-radius: 5px; border: 1px solid #fcd34d; background: #ffffff; color: #d97706; cursor: pointer; transition: all 0.15s ease;"
            >
              🟠 Withdraw
            </button>
          </div>
        </div>
      `;
    });

    const htmlContent = `
      <div style="font-size: 13.5px; color: #334155; line-height: 1.5; margin-bottom: 12px; text-align: left;">
        You are updating pipeline stage for <b>${candName}</b> for the role <b>"${roleTitle}"</b>${clientName}
      </div>

      <!-- Action Status Feedback Banner -->
      <div 
        id="perf-action-banner" 
        style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 10px 14px; margin-bottom: 12px; display: flex; align-items: center; justify-content: space-between; gap: 10px; text-align: left;"
      >
        <div id="perf-action-text" style="font-size: 13px; font-weight: 700; color: #3b5bfd;">
          🟢 Action: Move to <strong>${initialStage.stepName || 'Current Stage'}</strong> (Active)
        </div>
        <span 
          id="perf-action-badge" 
          style="font-size: 11px; font-weight: 800; padding: 3px 8px; border-radius: 12px; background: #dbeafe; color: #3b5bfd; text-transform: uppercase; letter-spacing: 0.5px; flex-shrink: 0;"
        >
          ACTIVE
        </span>
      </div>

      <div style="text-align: left; margin-bottom: 6px;">
        <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px;">
          Pipeline Stage & Action (Click step to advance, or click 🔴/⏸/🟠 to reject/hold/withdraw) <span style="color: #ef4444;">*</span>
        </label>
      </div>

      <!-- Scrollable Stage List -->
      <div 
        id="perf-stage-list-container" 
        style="max-height: 250px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; padding: 6px; margin-bottom: 12px;"
      >
        ${stagesListHtml}
      </div>

      <div style="text-align: left; margin-top: 10px;">
        <label id="perf-notes-label" style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 4px;">
          Stage Notes / Remarks (Optional)
        </label>
        <textarea 
          id="perf-candidate-notes" 
          placeholder="Add candidate evaluation, remarks, or notes for this stage..." 
          rows="3" 
          style="width: 100%; padding: 8px 10px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13px; font-family: inherit; color: #0f172a; box-sizing: border-box; outline: none; resize: vertical; line-height: 1.45;"
        >${cand.latestNotes ? cand.latestNotes : ''}</textarea>
      </div>
    `;

    let selectedStepIndex = initialSelectedIdx;
    let selectedOutcomeType = 'Active'; // 'Active' | 'Rejected' | 'Withdrawn' | 'Hold'

    const confirmResult = await Swal.fire({
      title: 'Update Pipeline Stage',
      html: htmlContent,
      width: '560px',
      showCancelButton: true,
      confirmButtonText: 'Yes, Submit',
      confirmButtonColor: '#229C8B',
      cancelButtonText: 'Cancel',
      didOpen: () => {
        const bannerEl = document.getElementById('perf-action-banner');
        const bannerTextEl = document.getElementById('perf-action-text');
        const bannerBadgeEl = document.getElementById('perf-action-badge');
        const notesLabelEl = document.getElementById('perf-notes-label');
        const notesEl = document.getElementById('perf-candidate-notes');
        const confirmBtn = Swal.getConfirmButton();

        const updateState = (idx, outcomeType) => {
          selectedStepIndex = idx;
          selectedOutcomeType = outcomeType;
          const currentStage = stagesToUse[idx] || {};
          const stageName = currentStage.stepName || `Stage ${idx + 1}`;

          // Update rows and buttons
          stagesToUse.forEach((s, sIdx) => {
            const row = document.getElementById(`swal-stage-row-${sIdx}`);
            const radio = document.getElementById(`swal-stage-radio-${sIdx}`);
            const rejBtn = document.getElementById(`swal-reject-btn-${sIdx}`);
            const holdBtn = document.getElementById(`swal-hold-btn-${sIdx}`);
            const wdrBtn = document.getElementById(`swal-withdraw-btn-${sIdx}`);

            if (sIdx === idx) {
              if (radio) radio.checked = true;
              if (outcomeType === 'Rejected') {
                if (row) { row.style.background = '#fef2f2'; row.style.border = '1.5px solid #ef4444'; }
                if (rejBtn) { rejBtn.style.background = '#dc2626'; rejBtn.style.color = '#ffffff'; rejBtn.style.border = '1px solid #dc2626'; }
                if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
                if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
              } else if (outcomeType === 'Hold') {
                if (row) { row.style.background = '#f5f3ff'; row.style.border = '1.5px solid #7c3aed'; }
                if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
                if (holdBtn) { holdBtn.style.background = '#7c3aed'; holdBtn.style.color = '#ffffff'; holdBtn.style.border = '1px solid #7c3aed'; }
                if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
              } else if (outcomeType === 'Withdrawn') {
                if (row) { row.style.background = '#fffbeb'; row.style.border = '1.5px solid #f59e0b'; }
                if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
                if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
                if (wdrBtn) { wdrBtn.style.background = '#d97706'; wdrBtn.style.color = '#ffffff'; wdrBtn.style.border = '1px solid #d97706'; }
              } else {
                if (row) { row.style.background = '#f0fdfa'; row.style.border = '1.5px solid #0d9488'; }
                if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
                if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
                if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
              }
            } else {
              if (radio) radio.checked = false;
              if (row) { row.style.background = '#ffffff'; row.style.border = '1px solid #e2e8f0'; }
              if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
              if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
              if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
            }
          });

          // Update Banner and Confirm Button
          if (outcomeType === 'Rejected') {
            if (bannerEl) { bannerEl.style.background = '#fef2f2'; bannerEl.style.border = '1px solid #fca5a5'; }
            if (bannerTextEl) { bannerTextEl.innerHTML = `🔴 Action: REJECT candidate at <strong>${stageName}</strong>`; bannerTextEl.style.color = '#b91c1c'; }
            if (bannerBadgeEl) { bannerBadgeEl.innerText = 'REJECTED'; bannerBadgeEl.style.background = '#fee2e2'; bannerBadgeEl.style.color = '#dc2626'; }
            if (notesLabelEl) { notesLabelEl.innerHTML = `Rejection Reason (Required) <span style="color: #ef4444;">*</span>`; }
            if (notesEl) { notesEl.placeholder = `Please provide the reason for rejection at ${stageName}...`; notesEl.style.borderColor = '#fca5a5'; notesEl.focus(); }
            if (confirmBtn) { confirmBtn.innerText = 'Confirm Rejection'; confirmBtn.style.backgroundColor = '#ef4444'; }
          } else if (outcomeType === 'Hold') {
            if (bannerEl) { bannerEl.style.background = '#f5f3ff'; bannerEl.style.border = '1px solid #c4b5fd'; }
            if (bannerTextEl) { bannerTextEl.innerHTML = `⏸ Action: Place candidate on HOLD at <strong>${stageName}</strong>`; bannerTextEl.style.color = '#6d28d9'; }
            if (bannerBadgeEl) { bannerBadgeEl.innerText = 'ON HOLD'; bannerBadgeEl.style.background = '#ede9fe'; bannerBadgeEl.style.color = '#7c3aed'; }
            if (notesLabelEl) { notesLabelEl.innerHTML = `Hold Reason (Required) <span style="color: #7c3aed;">*</span>`; }
            if (notesEl) { notesEl.placeholder = `Please provide the reason for placing candidate on hold at ${stageName}...`; notesEl.style.borderColor = '#c4b5fd'; notesEl.focus(); }
            if (confirmBtn) { confirmBtn.innerText = 'Confirm Hold'; confirmBtn.style.backgroundColor = '#7c3aed'; }
          } else if (outcomeType === 'Withdrawn') {
            if (bannerEl) { bannerEl.style.background = '#fffbeb'; bannerEl.style.border = '1px solid #fcd34d'; }
            if (bannerTextEl) { bannerTextEl.innerHTML = `🟠 Action: WITHDRAW candidate at <strong>${stageName}</strong>`; bannerTextEl.style.color = '#b45309'; }
            if (bannerBadgeEl) { bannerBadgeEl.innerText = 'WITHDRAWN'; bannerBadgeEl.style.background = '#fef3c7'; bannerBadgeEl.style.color = '#d97706'; }
            if (notesLabelEl) { notesLabelEl.innerHTML = `Withdrawal Reason (Required) <span style="color: #d97706;">*</span>`; }
            if (notesEl) { notesEl.placeholder = `Please provide the reason for withdrawal at ${stageName}...`; notesEl.style.borderColor = '#fcd34d'; notesEl.focus(); }
            if (confirmBtn) { confirmBtn.innerText = 'Confirm Withdrawal'; confirmBtn.style.backgroundColor = '#d97706'; }
          } else {
            if (bannerEl) { bannerEl.style.background = '#eff6ff'; bannerEl.style.border = '1px solid #bfdbfe'; }
            if (bannerTextEl) { bannerTextEl.innerHTML = `🟢 Action: Move to <strong>${stageName}</strong> (Active)`; bannerTextEl.style.color = '#3b5bfd'; }
            if (bannerBadgeEl) { bannerBadgeEl.innerText = 'ACTIVE'; bannerBadgeEl.style.background = '#eff6ff'; bannerBadgeEl.style.color = '#3b5bfd'; }
            if (notesLabelEl) { notesLabelEl.innerHTML = `Stage Notes / Remarks (Optional)`; }
            if (notesEl) { notesEl.placeholder = `Add candidate evaluation, remarks, or notes for this stage...`; notesEl.style.borderColor = '#cbd5e1'; }
            if (confirmBtn) { confirmBtn.innerText = 'Yes, Submit'; confirmBtn.style.backgroundColor = '#3b5bfd'; }
          }
        };

        // Attach click handlers
        stagesToUse.forEach((s, idx) => {
          const row = document.getElementById(`swal-stage-row-${idx}`);
          const selectTarget = row ? row.querySelector('.swal-stage-select-target') : null;
          const radio = document.getElementById(`swal-stage-radio-${idx}`);
          const rejBtn = document.getElementById(`swal-reject-btn-${idx}`);
          const holdBtn = document.getElementById(`swal-hold-btn-${idx}`);
          const wdrBtn = document.getElementById(`swal-withdraw-btn-${idx}`);

          const selectActive = () => updateState(idx, 'Active');
          if (selectTarget) selectTarget.addEventListener('click', selectActive);
          if (radio) radio.addEventListener('click', selectActive);

          if (rejBtn) {
            rejBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              updateState(idx, 'Rejected');
            });
          }

          if (holdBtn) {
            holdBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              updateState(idx, 'Hold');
            });
          }

          if (wdrBtn) {
            wdrBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              updateState(idx, 'Withdrawn');
            });
          }
        });
      },
      preConfirm: () => {
        const stageObj = stagesToUse[selectedStepIndex];
        if (!stageObj) {
          Swal.showValidationMessage('Please select a pipeline stage');
          return false;
        }

        const notesEl = document.getElementById('perf-candidate-notes');
        const notesVal = notesEl ? notesEl.value.trim() : '';

        if ((selectedOutcomeType === 'Rejected' || selectedOutcomeType === 'Withdrawn' || selectedOutcomeType === 'Hold') && !notesVal) {
          Swal.showValidationMessage(`Please enter a reason for ${selectedOutcomeType.toLowerCase()} at ${stageObj.stepName || stageObj.stageCode}`);
          return false;
        }

        return {
          stageObj,
          stepIndex: stageObj.stepIndex !== undefined ? stageObj.stepIndex : selectedStepIndex,
          stepName: stageObj.stepName || stageObj.stageCode,
          stageCode: stageObj.stageCode,
          phaseCode: selectedOutcomeType !== 'Active' ? 'OUTCOME' : (stageObj.phaseCode || 'INTERNAL'),
          outcomeType: selectedOutcomeType,
          notes: notesVal
        };
      }
    });

    if (!confirmResult.isConfirmed || !confirmResult.value) return;

    const { stepIndex, stepName, stageCode, phaseCode, outcomeType, notes } = confirmResult.value;

    let status = 'Active';
    let targetStageCode = stageCode;
    if (outcomeType === 'Rejected') {
      status = 'Rejected';
      targetStageCode = 'REJECTED';
    } else if (outcomeType === 'Hold') {
      status = 'Hold';
      targetStageCode = 'HOLD';
    } else if (outcomeType === 'Withdrawn') {
      status = 'Withdrawn';
      targetStageCode = 'WITHDRAWN';
    } else if (stageCode === 'HIRED' || stageCode === 'OFFER_ACCEPTED' || stageCode === 'PLACED') {
      status = 'Hired';
    } else {
      status = 'Active';
    }

    setUpdatingStageId(cand.id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BASE_URL}/api/recruitment/applications/${cand.id}/status`,
        {
          currentStep: stepIndex,
          status,
          stepName,
          stageCode: targetStageCode,
          rejectedAtStage: outcomeType !== 'Active' ? stepName : null,
          notes,
          entityId: 4
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // 1. Optimistically update candidate in local submissions array
      setCandidateSubmissions(prev => prev.map(item => {
        if (item.id === cand.id) {
          return {
            ...item,
            currentStageName: stepName,
            stageCode,
            phaseCode,
            currentStep: stepIndex,
            status: status.toUpperCase(),
            latestNotes: notes || item.latestNotes,
            latestNotesBy: notes ? 'You' : item.latestNotesBy
          };
        }
        return item;
      }));

      // 2. Silently refresh recruiter breakdown metrics so numbers update in real-time
      fetchReportData();

      // 3. Show brief toast notification
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `Stage updated to ${stepName} (${status})`,
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true
      });
    } catch (err) {
      console.error('Failed to update stage:', err);
      Swal.fire('Update Failed', err.response?.data?.message || 'Failed to update candidate stage.', 'error');
    } finally {
      setUpdatingStageId(null);
    }
  };

    // Filter and sort report data in table by region recruiters list
  const filteredReportData = useMemo(() => {
    const validRecruiterNames = new Set(recruitersList.map(r => (r.name || '').trim().toLowerCase()));

    let data = [...reportData];
    if (validRecruiterNames.size > 0) {
      data = data.filter(row => row.isOtherSubmission === 1 || validRecruiterNames.has((row.recruiterName || '').trim().toLowerCase()));
    }

    if (sortConfig.key) {
      data.sort((a, b) => {
        let valA, valB;
        if (sortConfig.key === 'recruiterName') {
          valA = (a.recruiterName || '').trim().toLowerCase();
          valB = (b.recruiterName || '').trim().toLowerCase();
          return sortConfig.direction === 'asc' 
            ? valA.localeCompare(valB) 
            : valB.localeCompare(valA);
        }

        switch (sortConfig.key) {
          case 'totalProfiles':
            valA = a.totalProfiles || 0;
            valB = b.totalProfiles || 0;
            break;
          case 'screened':
            valA = a.screenedCurrent || 0;
            valB = b.screenedCurrent || 0;
            break;
          case 'clientSubmit':
            valA = a.clientSubmitCurrent || 0;
            valB = b.clientSubmitCurrent || 0;
            break;
          case 'l1':
            valA = a.l1Current || 0;
            valB = b.l1Current || 0;
            break;
          case 'l2':
            valA = a.l2Current || 0;
            valB = b.l2Current || 0;
            break;
          case 'closure':
            valA = a.closureCurrent || 0;
            valB = b.closureCurrent || 0;
            break;
          case 'rejected':
            valA = a.rejectedCurrent || 0;
            valB = b.rejectedCurrent || 0;
            break;
          default:
            return 0;
        }

        if (valA === valB) {
          const nameA = (a.recruiterName || '').trim().toLowerCase();
          const nameB = (b.recruiterName || '').trim().toLowerCase();
          return nameA.localeCompare(nameB);
        }

        return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
      });
    }

    return data;
  }, [reportData, recruitersList, sortConfig]);

  // Calculate totals for summary cards
  const summaryTotals = useMemo(() => {
    const validRecruiterNames = new Set(recruitersList.map(r => (r.name || '').trim().toLowerCase()));
    const validData = validRecruiterNames.size > 0 
      ? reportData.filter(row => row.isOtherSubmission === 1 || validRecruiterNames.has((row.recruiterName || '').trim().toLowerCase()))
      : reportData;

    return validData.reduce((acc, row) => {
      acc.totalProfiles += row.totalProfiles || 0;
      acc.screened += row.screenedCount || 0;
      acc.clientSubmit += row.clientSubmitCount || 0;
      acc.l1 += row.l1Count || 0;
      acc.l2 += row.l2Count || 0;
      acc.closure += row.closureCount || 0;
      acc.rejected += row.rejectedCount || 0;

      acc.screenedCurrent += row.screenedCurrent || 0;
      acc.clientSubmitCurrent += row.clientSubmitCurrent || 0;
      acc.l1Current += row.l1Current || 0;
      acc.l2Current += row.l2Current || 0;
      acc.closureCurrent += row.closureCurrent || 0;
      acc.rejectedCurrent += row.rejectedCurrent || 0;
      acc.newCurrent += row.newCurrent || 0;

      return acc;
    }, { 
      totalProfiles: 0, 
      screened: 0, clientSubmit: 0, l1: 0, l2: 0, closure: 0, rejected: 0,
      screenedCurrent: 0, clientSubmitCurrent: 0, l1Current: 0, l2Current: 0, closureCurrent: 0, rejectedCurrent: 0, newCurrent: 0
    });
  }, [reportData, recruitersList]);

  // Excel Export
  const handleExportExcel = () => {
    if (filteredReportData.length === 0) return;

    const exportData = filteredReportData.map(row => ({
      'Recruiter': row.recruiterName,
      'Total Profiles': row.totalProfiles || 0,
      'Screened (Current Stage)': row.screenedCurrent || 0,
      'Client Submit (Current Stage)': row.clientSubmitCurrent || 0,
      'L1 Interview (Current Stage)': row.l1Current || 0,
      'L2 Interview (Current Stage)': row.l2Current || 0,
      'Closure / Hired': row.closureCurrent || 0,
      'Rejected / Withdrawn': row.rejectedCurrent || 0,
      'New / Pending': row.newCurrent || 0
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `${region} Recruiter Performance`);

    const dateSuffix = startDate && endDate ? `_${startDate}_to_${endDate}` : '';
    XLSX.writeFile(workbook, `${region}_Recruiter_Performance_Report${dateSuffix}.xlsx`);
  };

  // Download Report Dropdown Handler
  const handleDownloadReport = async (reportType) => {
    setIsReportDropdownOpen(false);

    if (reportType === 'excel') {
      handleExportExcel();
      return;
    }

    if (reportType === 'standard') {
      generateRecruiterPerformanceReportHtml({
        startDate,
        endDate,
        selectedRecruiter,
        timeframe,
        region,
        summaryTotals,
        reportData: filteredReportData
      });
      return;
    }

    const today = new Date();
    let repStart = startDate;
    let repEnd = endDate;

    if (reportType === 'today') {
      repStart = toYMD(today);
      repEnd = toYMD(today);
    } else if (reportType === 'weekly') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      const startOfWeek = new Date(today.getFullYear(), today.getMonth(), diff);
      const endOfWeek = new Date(today.getFullYear(), today.getMonth(), diff + 4);
      repStart = toYMD(startOfWeek);
      repEnd = toYMD(endOfWeek);
    } else if (reportType === 'monthly') {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      repStart = toYMD(startOfMonth);
      repEnd = toYMD(today);
    } else if (reportType === 'custom') {
      repStart = startDate || toYMD(today);
      repEnd = endDate || toYMD(today);
    }

    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/recruitment/applications/job-report/executive-data`, {
        params: {
          startDate: repStart,
          endDate: repEnd,
          region: region === 'Offshore' ? 'Offshore' : 'Onsite',
          client: 'all'
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      const execData = res.data || {};
      generateExecutiveDeckHtml({
        startDate: repStart,
        endDate: repEnd,
        regionFilter: region,
        selectedClient: selectedRecruiter ? `Recruiter: ${selectedRecruiter}` : `All ${region} Recruiters`,
        summary: execData.summary || {
          totalOpenJobs: 0,
          totalSubmissions: summaryTotals.totalProfiles || 0,
          totalInterviews: (summaryTotals.l1Current || 0) + (summaryTotals.l2Current || 0),
          totalClosures: summaryTotals.closureCurrent || 0
        },
        podData: execData.podData || [],
        keyClosures: execData.keyClosures || [],
        interviewsAwaiting: execData.interviewsAwaiting || [],
        onboardingPipeline: execData.onboardingPipeline || []
      });
    } catch (err) {
      console.warn("Fallback to client-side recruiter HTML report:", err);
      generateRecruiterPerformanceReportHtml({
        startDate: repStart,
        endDate: repEnd,
        selectedRecruiter,
        timeframe,
        region,
        summaryTotals,
        reportData: filteredReportData
      });
    }
  };

  // Summary Cards Configuration
  const cardsConfig = [
    {
      label: 'Total Profiles',
      value: summaryTotals.totalProfiles,
      subtitle: 'All candidate submissions',
      color: '#3b82f6',
      borderAccent: '#3b82f6'
    },
    {
      label: 'Screened',
      value: summaryTotals.screenedCurrent,
      subtitle: 'Resume Review, Ready for Client',
      color: '#f59e0b',
      borderAccent: '#f59e0b'
    },
    {
      label: 'Client Submit',
      value: summaryTotals.clientSubmitCurrent,
      subtitle: 'Submitted to Client, Review',
      color: '#8b5cf6',
      borderAccent: '#8b5cf6'
    },
    {
      label: 'L1 Interview',
      value: summaryTotals.l1Current,
      subtitle: 'Client Interview 1',
      color: '#0284c7',
      borderAccent: '#0284c7'
    },
    {
      label: 'L2 Interview',
      value: summaryTotals.l2Current,
      subtitle: 'Client Interview 2, Final',
      color: '#3b5bfd',
      borderAccent: '#3b5bfd'
    },
    {
      label: 'Closure / Hired',
      value: summaryTotals.closureCurrent,
      subtitle: 'Offer, Hired, Placed',
      color: '#10b981',
      borderAccent: '#10b981'
    },
    {
      label: 'Rejected / Withdrawn',
      value: summaryTotals.rejectedCurrent,
      subtitle: 'Rejected & Withdrawn (Internal, Client)',
      color: '#ef4444',
      borderAccent: '#ef4444'
    }
  ];

  return (
    <div className="recruiter-performance-report" style={{ padding: '0', backgroundColor: 'transparent', fontFamily: 'inherit' }}>
      
      {/* ── Compact Header & Action Bar ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px',
        background: '#ffffff',
        padding: '10px 16px',
        borderRadius: '8px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        border: '1px solid #e2e8f0',
        marginBottom: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ color: '#1e293b', margin: 0, fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={22} color="#3b5bfd" />
            Recruiter Performance Report
          </h2>

          <span style={{
            fontSize: '11px',
            color: region === 'Onsite' ? '#1e40af' : '#2563eb',
            background: region === 'Onsite' ? '#eff6ff' : '#eff6ff',
            border: '1px solid #bfdbfe',
            padding: '2px 8px',
            borderRadius: '12px',
            fontWeight: 600
          }}>
            {region === 'Onsite' ? 'Onsite (US / Global Jobs)' : 'Offshore (India Jobs)'}
          </span>
        </div>

        {/* Onsite / Offshore region toggle at top right corner */}
        <div className="region-toggle" style={{ margin: 0 }}>
          <button
            type="button"
            className={`region-btn ${region === 'Onsite' ? 'active' : ''}`}
            onClick={() => handleRegionChange('Onsite')}
          >
            <Building2 size={14} /> Onsite
          </button>
          <button
            type="button"
            className={`region-btn ${region === 'Offshore' ? 'active' : ''}`}
            onClick={() => handleRegionChange('Offshore')}
          >
            <Globe size={14} /> Offshore
          </button>
        </div>
      </div>

      {/* ── Compact Filters Section ── */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '10px 14px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        marginBottom: '10px'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
          
          {/* Quick Date Dropdown */}
          <div style={{ minWidth: '160px', flex: '1 1 150px', maxWidth: '200px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
              <Calendar size={12} color="#3b5bfd" />
              Quick Date
            </label>
            <select
              value={quickDate}
              onChange={(e) => handleQuickDateSelect(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '12px',
                fontWeight: 600,
                color: '#1e293b',
                background: '#ffffff',
                outline: 'none',
                cursor: 'pointer',
                height: '34px'
              }}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="this_week">This Week</option>
              <option value="last_week">Last Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Custom Date Inputs */}
          {(quickDate === 'custom' || startDate || endDate) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingBottom: '1px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '3px' }}>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setQuickDate('custom'); }}
                  style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none', height: '34px', boxSizing: 'border-box' }}
                />
              </div>
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', marginTop: '14px' }}>to</span>
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '3px' }}>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setQuickDate('custom'); }}
                  style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none', height: '34px', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          )}

          {/* Searchable Recruiter Dropdown */}
          <SearchableRecruiterSelect
            recruiters={recruitersList}
            selectedRecruiter={selectedRecruiter}
            onSelect={(recName) => setSelectedRecruiter(recName)}
            region={region}
          />

          {/* Clear Filters */}
          {(quickDate !== 'all' || selectedRecruiter || startDate || endDate) && (
            <div style={{ paddingBottom: '1px' }}>
              <button
                onClick={handleClearFilters}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#fff1f2',
                  border: '1px solid #fecdd3',
                  color: '#e11d48',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '11.5px',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  height: '34px'
                }}
              >
                <RotateCcw size={12} /> Clear Filters
              </button>
            </div>
          )}

          {/* Download Report Dropdown (Right Side of Filters) */}
          <div style={{ position: 'relative', marginLeft: 'auto', paddingBottom: '1px' }} ref={reportDropdownRef}>
            <button
              onClick={() => setIsReportDropdownOpen(prev => !prev)}
              style={{
                backgroundColor: '#3b5bfd',
                color: '#ffffff',
                border: 'none',
                padding: '6px 14px',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 600,
                fontSize: '12px',
                height: '34px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                transition: 'background 0.2s'
              }}
            >
              <Download size={13} />
              Download Report
              <ChevronDown size={13} style={{ transform: isReportDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
            </button>

            {isReportDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                width: '270px',
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                border: '1px solid #e2e8f0',
                zIndex: 9999,
                overflow: 'hidden'
              }}>
                <div style={{ padding: '8px 12px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                  SELECT REPORT FORMAT
                </div>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('today')}
                  style={{
                    width: '100%', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '10px',
                    background: 'none', border: 'none', textAlign: 'left', fontSize: '12px', color: '#1e293b',
                    cursor: 'pointer', borderBottom: '1px solid #f8fafc', transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <Zap size={15} color="#3b5bfd" />
                  <div>
                    <div style={{ fontWeight: 600 }}>Today</div>
                    <div style={{ fontSize: '10.5px', color: '#64748b' }}>Single day pipeline review</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('weekly')}
                  style={{
                    width: '100%', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '10px',
                    background: 'none', border: 'none', textAlign: 'left', fontSize: '12px', color: '#1e293b',
                    cursor: 'pointer', borderBottom: '1px solid #f8fafc', transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <BarChart3 size={15} color="#3b5bfd" />
                  <div>
                    <div style={{ fontWeight: 700, color: '#3b5bfd' }}>Weekly (Executive Deck)</div>
                    <div style={{ fontSize: '10.5px', color: '#64748b' }}>Multi-slide review (Mon–Fri)</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('monthly')}
                  style={{
                    width: '100%', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '10px',
                    background: 'none', border: 'none', textAlign: 'left', fontSize: '12px', color: '#1e293b',
                    cursor: 'pointer', borderBottom: '1px solid #f8fafc', transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <Calendar size={15} color="#3b5bfd" />
                  <div>
                    <div style={{ fontWeight: 600 }}>Monthly</div>
                    <div style={{ fontSize: '10.5px', color: '#64748b' }}>Current month performance</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('custom')}
                  style={{
                    width: '100%', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '10px',
                    background: 'none', border: 'none', textAlign: 'left', fontSize: '12px', color: '#1e293b',
                    cursor: 'pointer', borderBottom: '1px solid #f8fafc', transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <SlidersHorizontal size={15} color="#3b5bfd" />
                  <div>
                    <div style={{ fontWeight: 600 }}>Selected Date Range</div>
                    <div style={{ fontSize: '10.5px', color: '#64748b' }}>
                      {startDate && endDate ? `${startDate} — ${endDate}` : 'Current Range'}
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('standard')}
                  style={{
                    width: '100%', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '10px',
                    background: 'none', border: 'none', textAlign: 'left', fontSize: '12px', color: '#1e293b',
                    cursor: 'pointer', borderBottom: '1px solid #f8fafc', transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <FileText size={15} color="#3b5bfd" />
                  <div>
                    <div style={{ fontWeight: 600 }}>Standard Recruiter Report</div>
                    <div style={{ fontSize: '10.5px', color: '#64748b' }}>Full performance table (PDF/Print)</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('excel')}
                  style={{
                    width: '100%', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '10px',
                    background: 'none', border: 'none', textAlign: 'left', fontSize: '12px', color: '#1e293b',
                    cursor: 'pointer', transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f0fdfb'; e.currentTarget.style.color = '#10b981'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <FileSpreadsheet size={15} color="#10b981" />
                  <div>
                    <div style={{ fontWeight: 600 }}>Export to Excel (.xlsx)</div>
                    <div style={{ fontSize: '10.5px', color: '#64748b' }}>Instant spreadsheet download</div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ color: '#b91c1c', marginBottom: '10px', padding: '8px 12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '12px' }}>
          {error}
        </div>
      )}

      {/* ── Compact Summary KPI Cards ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: '8px',
        marginBottom: '10px'
      }}>
        {cardsConfig.map((stat, idx) => (
          <div
            key={idx}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              padding: '10px 14px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              border: '1px solid #e2e8f0',
              borderTop: `3px solid ${stat.borderAccent}`,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: '75px',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease'
            }}
          >
            <div>
              <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                {stat.label}
              </span>
              <div style={{ fontSize: '20px', fontWeight: 800, color: stat.color, marginTop: '2px', lineHeight: '1.2' }}>
                {isLoading ? <Loader className="spin" size={16} /> : (stat.value || 0)}
              </div>
            </div>
            <span style={{ fontSize: '9.5px', color: '#94a3b8', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {stat.subtitle}
            </span>
          </div>
        ))}
      </div>

      {/* ── Data Table with Expandable Recruiter Drill-Down (Leave Management Panel Style) ── */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '18px 20px',
        border: '1px solid #e8eef8',
        boxShadow: '0 8px 30px -4px rgba(45, 75, 215, 0.08)',
        marginBottom: '20px'
      }}>
        {/* Table Header Controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <h2 style={{
            fontSize: '17px',
            fontWeight: 700,
            color: '#1e2432',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Users size={20} color="#2546e5" />
            Recruiter Breakdown
            <span style={{ marginLeft: '4px', fontSize: '13px', fontWeight: 'normal', color: '#718096' }}>
              ({filteredReportData.length} {region.toLowerCase()} recruiters
              {filteredReportData.some(r => r.isOtherSubmission === 1) && (
                <span style={{ fontWeight: 500, fontSize: '11.5px', color: '#64748b', marginLeft: '6px' }}>
                  ({filteredReportData.filter(r => !r.isOtherSubmission).length} in pods • {filteredReportData.filter(r => r.isOtherSubmission === 1).length} other submissions)
                </span>
              )})
            </span>
          </h2>
          <span style={{ fontSize: '12px', color: '#718096', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Lightbulb size={14} color="#f59e0b" />
            <em>Click any recruiter row to expand candidate submissions</em>
          </span>
        </div>

        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            <Loader className="spin" size={26} color="#2546e5" style={{ margin: '0 auto' }} />
            <p style={{ marginTop: '8px', fontSize: '13px' }}>Loading recruiter performance metrics...</p>
          </div>
        ) : filteredReportData.length === 0 ? (
          <div style={{ padding: '35px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
            No records found for the selected criteria.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e8eef8' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f4f7fc', borderBottom: '2px solid #e8eef8' }}>
                  <th style={{ padding: '10px 4px', width: '28px', textAlign: 'center' }}></th>
                  {renderSortHeader('recruiterName', 'Recruiter', '20%', 'left')}
                  {renderSortHeader('totalProfiles', 'Total Profiles', '9%', 'center')}
                  {renderSortHeader('screened', 'Screened', '8%', 'center')}
                  {renderSortHeader('clientSubmit', 'Client Submit', '10%', 'center')}
                  {renderSortHeader('l1', 'L1 Interview', '9%', 'center')}
                  {renderSortHeader('l2', 'L2 Interview', '9%', 'center')}
                  {renderSortHeader('closure', 'Closure', '8%', 'center')}
                  {renderSortHeader('rejected', 'Rejected / Withdrawn', '13%', 'center')}
                  <th style={{ padding: '10px 8px', fontSize: '12.5px', fontWeight: 650, color: '#475569', textAlign: 'center', whiteSpace: 'nowrap', width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReportData.map((row, index) => {
                  const isExpanded = expandedRecruiter === row.recruiterName;
                  const screened = row.screenedCurrent || 0;
                  const clientSubmit = row.clientSubmitCurrent || 0;
                  const l1 = row.l1Current || 0;
                  const l2 = row.l2Current || 0;
                  const closure = row.closureCurrent || 0;
                  const rejected = row.rejectedCurrent || 0;

                  return (
                    <React.Fragment key={index}>
                      {/* Main Recruiter Row */}
                      <tr 
                        onClick={() => toggleRecruiterSubmissions(row.recruiterName)}
                        style={{ 
                          borderBottom: isExpanded ? 'none' : '1px solid #e8eef8',
                          backgroundColor: isExpanded ? '#eff3ff' : '#ffffff',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease'
                        }} 
                        onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                        onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#ffffff'; }}
                      >
                        <td style={{ padding: '10px 4px', textAlign: 'center', width: '28px' }}>
                          {isExpanded ? <ChevronUp size={15} color="#2546e5" /> : <ChevronDown size={15} color="#94a3b8" />}
                        </td>
                        <td 
                          style={{ padding: '10px 8px', fontWeight: 600, color: '#1e2432', fontSize: '13px', whiteSpace: 'nowrap' }}
                          title={row.recruiterName}
                        >
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span style={{ fontWeight: 600, color: '#1e2432', fontSize: '13px', whiteSpace: 'nowrap' }}>
                              {row.recruiterName}
                            </span>
                            {isBenchRecruiter(row.recruiterName) && (
                              <span style={{
                                background: '#ffedd5',
                                color: '#c2410c',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                fontSize: '10.5px',
                                fontWeight: 700,
                                border: '1px solid #fed7aa',
                                lineHeight: '1.2',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                              }}>
                                Bench
                              </span>
                            )}
                            {row.isOtherSubmission === 1 && (
                              <span style={{
                                background: '#f1f5f9',
                                color: '#475569',
                                padding: '2px 7px',
                                borderRadius: '4px',
                                fontSize: '10.5px',
                                fontWeight: 700,
                                border: '1px solid #cbd5e1',
                                lineHeight: '1.2',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                              }}>
                                Other Submissions
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '10px 6px', textAlign: 'center', fontWeight: 700, color: '#1e2432', fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {row.totalProfiles || 0}
                        </td>
                        <td style={{ padding: '10px 6px', textAlign: 'center', fontSize: '12.5px', color: '#334155', whiteSpace: 'nowrap' }}>
                          {screened || 0}
                        </td>
                        <td style={{ padding: '10px 6px', textAlign: 'center', fontSize: '12.5px', color: '#334155', whiteSpace: 'nowrap' }}>
                          {clientSubmit || 0}
                        </td>
                        <td style={{ padding: '10px 6px', textAlign: 'center', fontSize: '12.5px', color: '#334155', whiteSpace: 'nowrap' }}>
                          {l1 || 0}
                        </td>
                        <td style={{ padding: '10px 6px', textAlign: 'center', fontSize: '12.5px', color: '#334155', whiteSpace: 'nowrap' }}>
                          {l2 || 0}
                        </td>
                        <td style={{ padding: '10px 6px', textAlign: 'center', color: '#059669', fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {closure || 0}
                        </td>
                        <td style={{ padding: '10px 6px', textAlign: 'center', color: '#e11d48', fontWeight: 600, fontSize: '12.5px', whiteSpace: 'nowrap' }}>
                          {rejected || 0}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'center', width: '120px', whiteSpace: 'nowrap' }}>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleRecruiterSubmissions(row.recruiterName); }}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              border: isExpanded ? '1px solid #2546e5' : '1px solid #cbd5e1',
                              background: isExpanded ? '#2546e5' : '#ffffff',
                              color: isExpanded ? '#ffffff' : '#334155',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => {
                              if (!isExpanded) {
                                e.currentTarget.style.borderColor = '#2546e5';
                                e.currentTarget.style.color = '#2546e5';
                                e.currentTarget.style.background = '#eff3ff';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isExpanded) {
                                e.currentTarget.style.borderColor = '#cbd5e1';
                                e.currentTarget.style.color = '#334155';
                                e.currentTarget.style.background = '#ffffff';
                              }
                            }}
                          >
                            {isExpanded ? 'Hide' : 'View Candidates'}
                          </button>
                        </td>
                      </tr>

                      {/* ── Expanded Drill-Down Submissions Panel ── */}
                      {isExpanded && (
                        <tr style={{ borderBottom: '2px solid #2546e5', backgroundColor: '#eff3ff' }}>
                          <td colSpan="10" style={{ padding: '0 16px 16px 16px' }}>
                            <div style={{
                              background: '#ffffff',
                              borderRadius: '8px',
                              border: '1px solid #dbe6fe',
                              boxShadow: '0 4px 14px rgba(45, 75, 215, 0.06)',
                              overflow: 'hidden'
                            }}>
                              {/* Subheader */}
                              <div style={{
                                padding: '10px 16px',
                                background: '#eff3ff',
                                borderBottom: '1px solid #dbe6fe',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <span style={{ fontSize: '13px', fontWeight: 700, color: '#1d3bc9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <User size={14} /> Candidate Submissions by {row.recruiterName} ({candidateSubmissions.length} profiles)
                                </span>
                                {startDate && endDate && (
                                  <span style={{ fontSize: '11px', color: '#1d3bc9', background: '#dbe6fe', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                                    📅 {startDate} to {endDate}
                                  </span>
                                )}
                              </div>

                              {loadingSubmissions ? (
                                <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                                  <Loader className="spin" size={20} color="#3b5bfd" style={{ margin: '0 auto' }} />
                                  <p style={{ marginTop: '6px', fontSize: '11px' }}>Fetching candidate profiles...</p>
                                </div>
                              ) : candidateSubmissions.length === 0 ? (
                                <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '11.5px' }}>
                                  No candidate submissions found for this recruiter in the selected date range.
                                </div>
                              ) : (
                                <div style={{ overflowX: 'auto', maxHeight: '300px' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '11.5px' }}>
                                    <thead style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                                      <tr>
                                        <th style={{ padding: '8px 10px', color: '#64748b', fontWeight: 700, fontSize: '9.5px', textTransform: 'uppercase' }}>Candidate</th>
                                        <th style={{ padding: '8px 10px', color: '#64748b', fontWeight: 700, fontSize: '9.5px', textTransform: 'uppercase' }}>Contact</th>
                                        <th style={{ padding: '8px 10px', color: '#64748b', fontWeight: 700, fontSize: '9.5px', textTransform: 'uppercase' }}>Job ID / Code</th>
                                        <th style={{ padding: '8px 10px', color: '#64748b', fontWeight: 700, fontSize: '9.5px', textTransform: 'uppercase' }}>Job Title</th>
                                        <th style={{ padding: '8px 10px', color: '#64748b', fontWeight: 700, fontSize: '9.5px', textTransform: 'uppercase' }}>Client</th>
                                        <th style={{ padding: '8px 10px', color: '#64748b', fontWeight: 700, fontSize: '9.5px', textTransform: 'uppercase' }}>Applied Date</th>
                                        <th style={{ padding: '8px 10px', color: '#64748b', fontWeight: 700, fontSize: '9.5px', textTransform: 'uppercase' }}>Current Stage</th>
                                        <th style={{ padding: '8px 10px', color: '#64748b', fontWeight: 700, fontSize: '9.5px', textTransform: 'uppercase' }}>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {candidateSubmissions.map((cand, cIdx) => (
                                        <tr key={cIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                          <td style={{ padding: '8px 10px', fontWeight: 700, color: '#1e293b' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                              {cand.candidateName || 'N/A'}
                                              {Boolean(cand.isBench || cand.is_bench) && (
                                                <span style={{
                                                  background: '#ffedd5',
                                                  color: '#c2410c',
                                                  padding: '1px 5px',
                                                  borderRadius: '3px',
                                                  fontSize: '9.5px',
                                                  fontWeight: 700,
                                                  border: '1px solid #fed7aa'
                                                }}>
                                                  Bench
                                                </span>
                                              )}
                                            </span>
                                          </td>
                                          <td style={{ padding: '8px 10px', color: '#64748b' }}>
                                            <div>{cand.candidateEmail || '—'}</div>
                                            {cand.candidatePhone && <div style={{ fontSize: '9.5px', color: '#94a3b8' }}>{cand.candidatePhone}</div>}
                                          </td>
                                          <td style={{ padding: '8px 10px', fontWeight: 700, color: '#3b5bfd' }}>
                                            <span
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const targetId = cand.roleId || cand.jobId;
                                                if (targetId) navigate(`/role-detail/${targetId}`);
                                              }}
                                              style={{ cursor: 'pointer', textDecoration: 'none' }}
                                              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                                              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                                              title={`Click to view Role Overview for ${cand.jobId}`}
                                            >
                                              {cand.jobId || 'N/A'}
                                            </span>
                                          </td>
                                          <td style={{ padding: '8px 10px', color: '#3b5bfd', fontWeight: 600 }}>
                                            <span
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const targetId = cand.roleId || cand.jobId;
                                                if (targetId) navigate(`/role-detail/${targetId}`);
                                              }}
                                              style={{ cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                                              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                                              title={`Click to open Role Overview: ${cand.jobTitle}`}
                                            >
                                              {cand.jobTitle || 'N/A'}
                                            </span>
                                          </td>
                                          <td style={{ padding: '8px 10px', color: '#64748b' }}>
                                            {cand.clientName || '—'}
                                          </td>
                                          <td style={{ padding: '8px 10px', color: '#64748b' }}>
                                            {cand.appliedDate || '—'}
                                          </td>
                                          <td style={{ padding: '8px 10px' }}>
                                            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                              {updatingStageId === cand.id ? (
                                                <span style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: '4px',
                                                  padding: '2px 8px',
                                                  borderRadius: '10px',
                                                  background: '#e2e8f0',
                                                  fontSize: '9.5px',
                                                  color: '#475569',
                                                  fontWeight: 600
                                                }}>
                                                  <Loader className="spin" size={10} color="#3b5bfd" /> Updating...
                                                </span>
                                              ) : (
                                                <button
                                                  type="button"
                                                  onClick={() => handleOpenStageModal(cand)}
                                                  style={{
                                                    fontSize: '9.5px',
                                                    padding: '2px 10px 2px 8px',
                                                    borderRadius: '10px',
                                                    fontWeight: 700,
                                                    cursor: 'pointer',
                                                    outline: 'none',
                                                    border: '1px solid',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    background: cand.status === 'REJECTED' || cand.stageCode?.includes('REJECT') ? '#fee2e2' :
                                                                cand.status === 'WITHDRAWN' || cand.stageCode?.includes('WITHDRAW') ? '#fef3c7' :
                                                                cand.status === 'HIRED' || cand.stageCode?.includes('HIRED') ? '#dcfce7' :
                                                                cand.stageCode?.includes('CLIENT') ? '#ede9fe' : '#e0f2fe',
                                                    color: cand.status === 'REJECTED' || cand.stageCode?.includes('REJECT') ? '#b91c1c' :
                                                           cand.status === 'WITHDRAWN' || cand.stageCode?.includes('WITHDRAW') ? '#d97706' :
                                                           cand.status === 'HIRED' || cand.stageCode?.includes('HIRED') ? '#15803d' :
                                                           cand.stageCode?.includes('CLIENT') ? '#6d28d9' : '#0369a1',
                                                    borderColor: cand.status === 'REJECTED' || cand.stageCode?.includes('REJECT') ? '#fca5a5' :
                                                                 cand.status === 'WITHDRAWN' || cand.stageCode?.includes('WITHDRAW') ? '#fcd34d' :
                                                                 cand.status === 'HIRED' || cand.stageCode?.includes('HIRED') ? '#86efac' :
                                                                 cand.stageCode?.includes('CLIENT') ? '#c4b5fd' : '#bae6fd',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                                    transition: 'all 0.15s ease'
                                                  }}
                                                  title="Click to update candidate stage & action"
                                                >
                                                  <span>{cand.currentStageName || cand.stageCode || 'New'}</span>
                                                  <ChevronDown size={10} style={{ opacity: 0.7 }} />
                                                </button>
                                              )}
                                              {cand.latestNotes && (
                                                <span 
                                                  title={`Stage Note: "${cand.latestNotes}"${cand.latestNotesBy ? ` (by ${cand.latestNotesBy})` : ''}`}
                                                  style={{
                                                    marginLeft: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '11px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: '2px 5px',
                                                    background: '#f0fdfa',
                                                    borderRadius: '4px',
                                                    border: '1px solid #99f6e4',
                                                    color: '#3b5bfd',
                                                    lineHeight: 1
                                                  }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    Swal.fire({
                                                      title: `Stage Note for ${cand.candidateName}`,
                                                      html: `
                                                        <div style="text-align: left; font-size: 13.5px; color: #334155; line-height: 1.5;">
                                                          <p style="margin-bottom: 6px;"><strong>Stage:</strong> <span style="color: #3b5bfd; font-weight: 700;">${cand.currentStageName || cand.stageCode || 'Current'}</span></p>
                                                          ${cand.latestNotesBy ? `<p style="margin-bottom: 8px; font-size: 12px; color: #64748b;"><strong>Updated by:</strong> ${cand.latestNotesBy}</p>` : ''}
                                                          <div style="background: #f8fafc; border: 1px solid #cbd5e1; border-radius: 6px; padding: 10px 12px; font-weight: 500; color: #0f172a; margin-top: 6px; white-space: pre-wrap;">
                                                            "${cand.latestNotes}"
                                                          </div>
                                                        </div>
                                                      `,
                                                      confirmButtonColor: '#3b5bfd',
                                                      confirmButtonText: 'Close'
                                                    });
                                                  }}
                                                >
                                                  📝
                                                </span>
                                              )}
                                            </div>
                                          </td>
                                          <td style={{ padding: '8px 10px' }}>
                                            <span style={{
                                              fontSize: '9.5px',
                                              padding: '1px 5px',
                                              borderRadius: '4px',
                                              fontWeight: 600,
                                              background: cand.status === 'HIRED' ? '#dcfce7' : cand.status === 'REJECTED' ? '#fee2e2' : '#f1f5f9',
                                              color: cand.status === 'HIRED' ? '#166534' : cand.status === 'REJECTED' ? '#991b1b' : '#475569'
                                            }}>
                                              {cand.status || 'ACTIVE'}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default RecruiterPerformanceReport;
